import { useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  CircularProgress,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  HookFormInputField,
  HookFormAutocompleteField,
  HookFormMultilineInputField
} from 'src/components/FormFields';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const BookingRequestForm = ({
  openDialog,
  setOpenDialog,
  locationOptions,
  tenantId,
  selectedService = '',
  selectedPractitioner = '',
  selectedLocation = '',
  onlineBookingPageId = '',
  kioskMode = false
}) => {
  const { onlineBookingId } = useParams();
  const selectedOnlineBookingId = onlineBookingId || onlineBookingPageId;
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  // Registering React Hook Form
  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      mobile: '',
      locationId: locationOptions[0]?.value,
      requestRemarks: ''
    }
  });

  const onSubmit = useCallback(
    async (data) => {
      if (!executeRecaptcha || !selectedOnlineBookingId) {
        console.error('reCAPTCHA is not ready yet.');
        return;
      }

      // Execute reCAPTCHA with an action name (e.g., "submit")
      const token = await executeRecaptcha('submitOnlineBookingRequest');

      // console.log('reCAPTCHA Token:', token);
      // console.log('submittt');
      const currentURL = window.location.href;

      // recaptch ready and passed.
      if (token) {
        const inputObject = {
          tenantId: tenantId,
          locationId: data?.locationId,
          customerName: data?.name,
          customerMobile: data?.mobile,
          customerEmail: data?.email,
          requestRemarks: `
          User Selection - Service: ${selectedService} - Location: ${selectedLocation} - Practitioner: ${selectedPractitioner} - URL: ${currentURL}
          <br/>
          User Remarks: ${data?.requestRemarks}`,
          onlineBookingPageId: selectedOnlineBookingId,
          reCaptchaToken: token,
          reCaptchaAction: 'submitOnlineBookingRequest'
          // status: 'Unread'
        };

        // console.log(inputObject);

        const graphQLquery = {
          // operationName: 'getOnlineBookingPage',
          query: `mutation createOnlineBookingRequest($input: CreateOnlineBookingRequestInput!) {\n  createOnlineBookingRequest(input: $input) {\n    id\n}\n}\n`,
          variables: { input: inputObject }
        };

        axios
          .post(process.env.REACT_APP_AWS_GRAPHQL_API, graphQLquery, {
            headers: {
              'x-api-key': process.env.REACT_APP_AWS_CONFIRMATION_API,
              accept: 'application/json, text/plain, */*',
              'accept-language': 'en-GB,en;q=0.9,en-US;q=0.8,vi;q=0.7',
              'content-type': 'application/json'
            }
          })
          .then((res) => {
            console.log(res);
            reset();
            if (kioskMode) {
              navigate('/kiosk/kiosk/checkin', { replace: true });
            } else {
              if (tenantId === '4e2bfe06-bd9f-434f-b524-9ec287abc92b') {
                window.location.href =
                  'https://www.chiropractic-singapore.com.sg/request-confirmation';
              } else if (tenantId === 'CityOsteoPhysio') {
                window.location.href =
                  'https://cityosteophysio.com/request-confirmation';
              } else {
                navigate(`/booking/success?tenant=${tenantId}`, {
                  replace: true
                });
              }
            }
          });
      }
    },
    [
      executeRecaptcha,
      selectedLocation,
      selectedPractitioner,
      selectedLocation,
      selectedService
    ]
  );

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="md"
        open={openDialog}
        // this special onClose function disables backdropClick for Modals and Dialogs
        onClose={(event, reason) => {
          if (reason && reason == 'backdropClick') {
            return;
          }
          reset();
          setOpenDialog(false);
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            display: 'inline-flex',
            justifyContent: 'space-between'
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="h3" gutterBottom>
              Appointment Timeslot Request
            </Typography>
            <Typography variant="subtitle2">
              Note that this is not a booking, and the clinic will contact you
              within 1 working day.
            </Typography>
          </Box>

          <Box
            display={{ xs: 'flex', sm: 'block' }}
            justifyContent="flex-end"
            alignItems="flex-start"
          >
            <IconButton
              color="primary"
              onClick={() => {
                reset();
                setOpenDialog(false);
              }}
            >
              <CloseTwoToneIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <HookFormInputField
                  name="name"
                  label="Name"
                  control={control}
                  errors={errors}
                  rules={{
                    required: true
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <HookFormInputField
                  name="email"
                  label="Email"
                  control={control}
                  errors={errors}
                  rules={{
                    required: true
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <HookFormInputField
                  name="mobile"
                  label="Mobile"
                  control={control}
                  errors={errors}
                  rules={{
                    required: true,
                    pattern: {
                      value: /^\d{7,12}$/,
                      message: 'Please enter a valid mobile number.'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <HookFormAutocompleteField
                  name="locationId"
                  label="Location"
                  options={locationOptions}
                  control={control}
                  errors={errors}
                  rules={{ required: 'Location is required.' }}
                  notClearable={true}
                />
              </Grid>

              <Grid item xs={12} md={12}>
                <HookFormMultilineInputField
                  name="requestRemarks"
                  label="Please include Areas of Concern and Preferred Date and Time"
                  control={control}
                  errors={errors}
                  rules={{
                    required: true
                  }}
                  rows={5}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions
            sx={{
              p: 3
            }}
          >
            <Button
              type="submit"
              startIcon={isSubmitting ? <CircularProgress size="1rem" /> : null}
              disabled={isSubmitting}
              variant="contained"
            >
              {'Submit'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default BookingRequestForm;
